import React, { useContext, useState, useRef } from 'react'
import { UiContext } from 'context/ui'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { FormCustomSelect } from 'components/Form'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

import { ReactComponent as TikTokIcon } from 'i/icons/social_media/logo-tiktok.svg'
import { ReactComponent as YoutubeIcon } from 'i/icons/social_media/youtube.svg'
import { ReactComponent as FacebookIcon } from 'i/icons/social_media/facebook2.svg'
import { ReactComponent as InstagramIcon } from 'i/icons/social_media/instagram2.svg'
import { ReactComponent as LinkedinIcon } from 'i/icons/social_media/linkedin2.svg'
import { ReactComponent as TwitterIcon } from 'i/icons/social_media/twitter2.svg'

import { profileApi, authApi, paymentApi } from 'api'

import { logIn } from 'store/actions'

import { timezoneOptions, professionalTypeOptions } from 'utils/constants'

import { useClickOutside } from 'hooks'
import Popup from '../Popup/Popup'

import { AlertMessage } from 'components/AlertMessage'
import FormInputWithLabel from 'components/Form/FormInputWithLabel/FormInputWithLabel'
import { Spinner } from 'components/Spinner'

const normalizeHandle = (value, platform) => {
  if (!value) return ''

  let normalized = value.trim()
  if (!normalized) return ''

  normalized = normalized.replace(/^https?:\/\//i, '').replace(/^www\./i, '')

  const slashIndex = normalized.indexOf('/')
  const host = slashIndex >= 0 ? normalized.slice(0, slashIndex).toLowerCase() : ''
  const path = slashIndex >= 0 ? normalized.slice(slashIndex + 1) : normalized
  const segments = path.split('/').filter(Boolean)

  if (platform === 'linkedin' && host.includes('linkedin.com')) {
    const inIndex = segments.indexOf('in')
    normalized = inIndex >= 0 ? segments[inIndex + 1] || '' : segments[0] || ''
  } else if (platform === 'instagram' && host.includes('instagram.com')) {
    normalized = segments[0] || ''
  } else if (platform === 'facebook' && host.includes('facebook.com')) {
    normalized = segments[0] || ''
  } else if (
    platform === 'twitter' &&
    (host.includes('twitter.com') || host.includes('x.com'))
  ) {
    normalized = segments[0] || ''
  } else if (platform === 'tiktok' && host.includes('tiktok.com')) {
    const segment = segments[0] || ''
    normalized = segment.startsWith('@') ? segment.slice(1) : segment
  }

  return normalized.replace(/^@+/, '').trim()
}

const normalizeYoutube = (value) => {
  if (!value) return ''

  let normalized = value.trim()
  if (!normalized) return ''

  if (/^https?:\/\//i.test(normalized)) {
    return normalized
  }

  const handle = normalized.replace(/^@+/, '')
  if (!handle) return ''

  return `https://www.youtube.com/@${handle}`
}

const ProfilePopup = (props) => {
  const $container = useRef()
  const avatarInputRef = useRef(null)
  const history = useHistory()
  const [errorSubmitMessage, setErrorSubmitMessage] = useState(null)
  const { setActivePopupId } = useContext(UiContext)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [timezone, setTimezone] = useState({ name: 'America/New_York', tag: 'en-US' })

  const { isOpen } = props

  const handleClose = () => {
    setActivePopupId(null)
  }

  const openSubscriptionPopup = () => {
    setIsLoading(true)
    paymentApi.getSubscriptionOptions().then((res) => {
      if (res?.result?.session_url) {
        window.location = res?.result?.session_url
      } else {
        setIsLoading(false)
        setErrorSubmitMessage('Something went wrong, please try again later.')
      }
    })
  }

  const handleAvatarUpload = async (event) => {
    const file = event?.target?.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('photo', file)

    setIsLoading(true)
    setErrorSubmitMessage(null)

    const uploadRes = await profileApi.uploadPhoto(formData)
    if (uploadRes?.error || !uploadRes?.result?.fileUrl) {
      setIsLoading(false)
      setErrorSubmitMessage('Unable to upload avatar. Please try again.')
      return
    }

    const saveRes = await profileApi.setProInfo({ photo: uploadRes.result.fileUrl })
    if (saveRes?.error) {
      setIsLoading(false)
      setErrorSubmitMessage('Unable to save avatar. Please try again.')
      return
    }

    window.location.reload()
  }

  useClickOutside($container, handleClose)

  const rateSchema = Yup.object().shape({
    rate: Yup.number().required('Rate is required').max(10000, 'Rate is too high'),
  })

  return (
    <Popup
      isOpen={isOpen}
      title="Account"
      description="Manage your profile and subscription"
      headBorder
    >
      <Formik
        initialValues={{
          rate: Math.trunc(props.user?.rate),
          location: `${props.user?.location ? props.user?.location : ''}`,
          // Need to review
          language: `${
            props.user?.language ? props.user?.language?.code?.toLowerCase() : 'en'
          }`,
          category: props.user?.category || '',
          website: `${props.user?.youtube ? props.user?.youtube : ''}`,
          expectations: `${props.user?.expectations ? props.user?.expectations : ''}`,
          bio: `${props.user?.bio ? props.user?.bio : ''}`,
          name: `${props.user?.user?.name ? props.user?.user?.name : ''}`,
          website_url: `${props.user?.website_url ? props.user?.website_url : ''}`,
          linkedin_usr: `${props.user?.linkedin_usr ? props.user?.linkedin_usr : ''}`,
          insta_usr: `${props.user?.insta_usr ? props.user?.insta_usr : ''}`,
          facebook_usr: `${props.user?.facebook_usr ? props.user?.facebook_usr : ''}`,
          twitter_usr: `${props.user?.twitter_usr || ''}`,
          youtube: `${props.user?.youtube || ''}`,
          tiktok_usr: `${props.user?.tiktok_usr || ''}`,
        }}
        validationSchema={rateSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setErrorSubmitMessage(null)
          const data = {
            rate: values.rate.toString(),
            rate_currency_code: 'USD',
            language: props.user?.language
              ? props.user?.language?.code?.toLowerCase()
              : 'en',
            location: values.location,
            category: values.category || '',
            expectations: values.expectations,
            bio: values.bio,
            name: values.name,
            website_url: values.website_url,
            linkedin_usr: normalizeHandle(values.linkedin_usr, 'linkedin'),
            insta_usr: normalizeHandle(values.insta_usr, 'instagram'),
            facebook_usr: normalizeHandle(values.facebook_usr, 'facebook'),
            twitter_usr: normalizeHandle(values.twitter_usr, 'twitter'),
            youtube: normalizeYoutube(values.youtube),
            tiktok_usr: normalizeHandle(values.tiktok_usr, 'tiktok'),
            timezone: timezone.name,
          }

          Object.keys(data).forEach((key) => {
            if (data[key] === '') delete data[key]
          })

          const res = await profileApi.setProInfo(data)
          if (res?.error) {
            setErrorSubmitMessage(
              res?.error?.data?.error ||
                'Unable to save profile. Please check your inputs.'
            )
            setSubmitting(false)
            return
          }

          setSuccess(true)

          window.location.reload()
        }}
      >
        {({ errors, touched, submitForm, isSubmitting, handleChange, values }) => (
          <>
            <div className="signup__tabs_content signup__tabs_content--border_mod">
              <div className="signup__tabs_content_item signup__tabs_content_item--active">
                <div className="signup__form">
                  {errorSubmitMessage && (
                    <AlertMessage message={errorSubmitMessage} formMod />
                  )}
                  {props?.user?.subscription_timestamp && (
                    <div className="form_group">
                      <label className="form_group__label">Subscription</label>
                      {isLoading ? (
                        <Spinner
                          className="form_group__button spinner_mod"
                          button={true}
                        />
                      ) : (
                        <button
                          type="button"
                          className="form_group__field form_group__field--button_mod"
                          onClick={openSubscriptionPopup}
                        >
                          Manage Subscription
                        </button>
                      )}
                    </div>
                  )}
                  <Form className="signup__form_in">
                    <div className="form_group form_group--icon_mod profile_mod">
                      <div className="form_group profile_mod">
                        <label className="form_group__label">Pro Type</label>
                        <Field
                          name="category"
                          id="category"
                          as="select"
                          className="form_group__field"
                          disabled={isSubmitting || success}
                          onChange={(e) => {
                            handleChange(e)
                          }}
                        >
                          <option value="Professional Type" disabled hidden>
                            Professional Type
                          </option>
                          {professionalTypeOptions?.map((option) => (
                            <option key={option.value} value={option.value?.name}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                      </div>
                    </div>
                    <div class="form_group">
                      <FormCustomSelect
                        options={timezoneOptions}
                        onChange={(e) => {
                          setTimezone(e.value)
                        }}
                      />
                    </div>
                    <div className="form_group">
                      <FormInputWithLabel label="Display name">
                        <Field
                          className="form_group__field"
                          name="name"
                          type="text"
                          disabled={isSubmitting || success}
                          onChange={(e) => handleChange(e)}
                        />
                      </FormInputWithLabel>
                    </div>
                    <div className="form_group">
                      <label className="form_group__label">Avatar</label>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleAvatarUpload}
                      />
                      <button
                        type="button"
                        className="form_group__field form_group__field--button_mod"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={isSubmitting || success || isLoading}
                      >
                        Change Avatar
                      </button>
                    </div>
                    <div className="form_group">
                      <FormInputWithLabel textarea label="Your bio (max 400 char)">
                        <Field
                          className="form_group__field"
                          name="bio"
                          as="textarea"
                          disabled={isSubmitting || success}
                          onChange={(e) => handleChange(e)}
                        />
                      </FormInputWithLabel>
                    </div>
                    <div className="form_group">
                      <FormInputWithLabel label="Enter website url">
                        <Field
                          className="form_group__field"
                          name="website_url"
                          type="text"
                          disabled={isSubmitting || success}
                          onChange={(e) => handleChange(e)}
                        />
                      </FormInputWithLabel>
                    </div>
                    <div className="form_row form_row--grid_mod">
                      <div className="form_group form_group--icon_mod">
                        <FormInputWithLabel
                          type="text"
                          label="LinkedIn"
                          icon={<LinkedinIcon />}
                          iconPosition
                        >
                          <Field
                            className="form_group__field"
                            name="linkedin_usr"
                            type="text"
                            disabled={isSubmitting || success}
                            onChange={(e) => handleChange(e)}
                          />
                        </FormInputWithLabel>
                      </div>
                      <div className="form_group form_group--icon_mod">
                        <FormInputWithLabel
                          type="text"
                          label="Instagram"
                          icon={<InstagramIcon />}
                          iconPosition
                        >
                          <Field
                            className="form_group__field"
                            name="insta_usr"
                            type="text"
                            disabled={isSubmitting || success}
                            onChange={(e) => handleChange(e)}
                          />
                        </FormInputWithLabel>
                      </div>
                      <div className="form_group form_group--icon_mod">
                        <FormInputWithLabel
                          type="text"
                          label="Facebook"
                          icon={<FacebookIcon />}
                          iconPosition
                        >
                          <Field
                            className="form_group__field"
                            name="facebook_usr"
                            type="text"
                            disabled={isSubmitting || success}
                            onChange={(e) => handleChange(e)}
                          />
                        </FormInputWithLabel>
                      </div>
                      <div className="form_group form_group--icon_mod">
                        <FormInputWithLabel
                          type="text"
                          label="Twitter"
                          icon={<TwitterIcon />}
                          iconPosition
                        >
                          <Field
                            className="form_group__field"
                            name="twitter_usr"
                            type="text"
                            disabled={isSubmitting || success}
                            onChange={(e) => handleChange(e)}
                          />
                        </FormInputWithLabel>
                      </div>
                      <div className="form_group form_group--icon_mod">
                        <FormInputWithLabel
                          type="text"
                          label="YouTube Channel"
                          icon={<YoutubeIcon />}
                          iconPosition
                        >
                          <Field
                            className="form_group__field"
                            name="youtube"
                            type="text"
                            disabled={isSubmitting || success}
                            onChange={(e) => handleChange(e)}
                          />
                        </FormInputWithLabel>
                      </div>
                      <div className="form_group form_group--icon_mod">
                        <FormInputWithLabel
                          type="text"
                          label="TikTok"
                          icon={<TikTokIcon />}
                          iconPosition
                        >
                          <Field
                            className="form_group__field"
                            name="tiktok_usr"
                            type="text"
                            disabled={isSubmitting || success}
                            onChange={(e) => handleChange(e)}
                          />
                        </FormInputWithLabel>
                      </div>
                    </div>

                    {isSubmitting ? (
                      <Spinner className="form_group__button spinner_mod" button={true} />
                    ) : (
                      <div className="form_group">
                        <button
                          type="submit"
                          onClick={(e) => {
                            e.preventDefault(e)
                            submitForm()
                          }}
                          className="form_group__button"
                        >
                          Submit
                        </button>
                      </div>
                    )}
                    <button
                      onClick={(e) =>
                        authApi
                          .logOut()
                          .then((history.push('/'), window.localStorage.clear()))
                      }
                      className="form_group__button share_mod"
                    >
                      Log Out
                    </button>
                    <br />
                    <br />
                  </Form>
                </div>
              </div>
            </div>
          </>
        )}
      </Formik>
    </Popup>
  )
}

const mapStateToProps = ({ auth: { user, error, isPro } }) => ({
  authError: error,
  user,
  isPro,
})

const willMapDispatchToProps = {
  logIn,
}

export default connect(mapStateToProps, willMapDispatchToProps)(ProfilePopup)
